<script setup>
/**
 * Useful to display a button in a component.
 * The button will show the label unless the slot is filled.
 *
 * Reacts to the processing and recentlySuccessful state of a form.
 * If the form is processing, the button will show a spinner.
 * If the form was recently successful, the button will show a checkmark.
 *
 * Can be used as a link or a button, by setting the is prop to 'a' or 'button'.
 */
import ReactiveSlot from '@/Components/Buttons/Partials/ReactiveSlot.vue';
import {computed, useSlots} from 'vue';

const props = defineProps({
    label: String,

    form: Object,

    disabled: {
        type: Boolean,
        default: false,
    },

    white: Boolean,
    danger: Boolean,
    classes: String,
    remove_width: Boolean,
    small: Boolean,
});

const normal = !props.black && !props.white;

const slots = useSlots();

const hasError = computed(() => {
    return typeof props.form?.errors == 'undefined'
        ? false
        : Object.values(props.form.errors).some((error) => error.length > 0);
});
</script>
<template>
    <button
        type="button"
        class="flex-center relative rounded-sm uppercase transition duration-300 disabled:cursor-not-allowed disabled:opacity-50"
        :class="[{
            'border-red-600 bg-red-600 text-white ring-red-600 hover:bg-primary-700 hover:ring-primary-700 focus:ring-primary-500': danger || hasError,
            'border-primary bg-primary text-white ring-primary hover:bg-primary-800 hover:ring-primary-800 focus:ring-primary-600': normal && !hasError,
            'hover:bg-grey-50 border-primary bg-white text-primary ring-primary hover:ring-primary-800 focus:ring-primary-600': props.white && !hasError,
            'w-60': !remove_width && !small,
            'h-12 lg:h-13': !small,
            'size-12' : small,
            'cursor-not-allowed': disabled || form?.processing || form?.recentlySuccessful,
        },classes]"
        :disabled="disabled"
        :aria-label="label"
    >
        <ReactiveSlot
            :processing="form?.processing ?? false"
            :recently-successful="form?.recentlySuccessful ?? false"
        >
            <div
                v-if="!slots.default"
                class="text-center leading-tight"
            >
                {{ label }}
            </div>

            <slot />
        </ReactiveSlot>
    </button>
</template>
